<template>
  <div class="print">
    <div
      id="app-bg"
      :class="{
        background: isLoginPage || isSignupPage,
        landing: isLandingPage,
        wifisetup: isWifiSetupPage,
      }"
    />
    <v-app
      light
      :class="$desktopModeStyles"
    >
      <!-- Left drawer -->
      <app-drawer
        v-if="!fullscreenMode"
        :show.sync="showDrawer"
        :user-label="userLabel"
      />
      <!-- Exit fullscreen button -->
      <v-fade-transition>
        <v-btn
          v-if="canExitFullscreenMode"
          round
          small
          :title="$t('fullscreenDisableTooltip')"
          class="white"
          flat
          style="
            position: fixed;
            left: 50%;
            transform: translateX(-50%);
            z-index: 4;
            margin-top: 8px;
            max-height: 20px;
          "
          @click="$store.commit('SET_FULLSCREEN_MODE', false)"
        >
          {{ $t('fullscreenDisable') }}
        </v-btn>
      </v-fade-transition>
      <!-- Shadow Box -->
      <div
        v-if="!fullscreenMode"
        class="shadow-box elevation-24"
        :class="{
          ...$desktopModeStyles,
          background: isTopLevelPage,
        }"
      />
      <!-- Top toolbar -->
      <v-fade-transition>
        <v-toolbar
          v-if="!isLandingPage && !fullscreenMode"
          v-show="showToolbar"
          app
          dense
          fixed
          clipped-left
          :style="{
            'background-color': isTopLevelPage ? 'transparent' : 'initial',
          }"
          :class="{
            ...$desktopModeStyles,
            'elevation-0': !elevateToolbar,
            'elevation-1': elevateToolbar,
            'msr--bg-cool-gray': !isTopLevelPage && darkToolbar,
            white: !isTopLevelPage && !darkToolbar,
          }"
        >
          <v-toolbar-side-icon
            v-if="$store.getAuthToken()"
            :dark="darkToolbar"
            large
            data-cy="toggle-drawer-toolbar-btn"
            @click.stop="toggleDrawer()"
          >
            <v-icon v-if="!showDrawer && $vuetify.breakpoint.lgAndUp">
              chevron_right
            </v-icon>
          </v-toolbar-side-icon>
          <v-spacer v-if="isTopLevelPage" />
          <v-toolbar-title
            v-if="!isLoginPage && !isSignupPage"
            class="unselectable"
            style="display: flex; flex-grow: 0"
          >
            <router-link
              :to="{name: 'dashboard'}"
              style="display: flex; align-items: center"
            >
              <img
                class="toolbar-logo"
                :title="$t('toHomepage')"
                :src="toolbarImageURL"
              >
            </router-link>
          </v-toolbar-title>
          <div
            v-if="
              $store.state.common.dashboardInfo &&
                $store.state.common.dashboardInfo.is_production &&
                $userprofile &&
                $userprofile.is_being_impersonated
            "
            class="ml-3 font-weight-bold headline msr--text"
            style="letter-spacing: 1px !important"
          >
            PROD
          </div>
          <v-spacer />
          <!-- Enter Fullscreen Mode, if route and screen support it -->
          <v-btn
            v-if="canEnterFullscreenMode"
            flat
            round
            small
            dark
            class="mx-2"
            :title="$t('fullscreenEnableTooltip')"
            data-cy="enter-fullscreen-mode-btn"
            @click="enterFullscreen()"
          >
            <v-icon left>
              fullscreen
            </v-icon>
            {{ $t('fullscreenEnable') }}
          </v-btn>
          <v-spacer />
          <!-- Demo Account Warning -->
          <v-chip
            v-if="$userprofile && $userprofile.is_demo"
            small
            class="ml-1"
            :title="$t('isDemoAccount')"
          >
            Demo
          </v-chip>
          <!-- User Account Link -->
          <v-slide-x-reverse-transition>
            <div
              v-if="$user && userLabel && $vuetify.breakpoint.smAndUp"
              class="ml-4 clickable unselectable"
              style="display: flex; align-items: center"
              :title="$t('goToAccountPage')"
              @click="$router.push({name: 'account'})"
            >
              <v-badge
                color="transparent msr--text"
                left
                overlap
              >
                <span
                  v-if="$userprofile && $userprofile.is_being_impersonated"
                  slot="badge"
                  :title="$t('isBeingImpersonated')"
                  class="highlighted-badge-text"
                  data-hj-suppress
                  style="position: relative; top: 6px; left: -16px"
                >MY</span>
                <user-avatar-image
                  v-if="$userprofile.avatar_image_url"
                  :user="$user"
                  :size="36"
                  class="mr-3"
                />
                <v-icon
                  v-else
                  color="white"
                  large
                  data-hj-suppress
                  class="mr-3"
                >
                  person
                </v-icon>
              </v-badge>
              <span class="white--text subheading">
                {{ userLabel }}
              </span>
            </div>
          </v-slide-x-reverse-transition>
          <!-- Reload button for Android instead of swipe-to-refresh -->
          <v-toolbar-side-icon
            v-if="$store.state.common.runtimeEnv.android"
            :dark="darkToolbar"
            small
            class="ml-2"
            @click.stop="reloadApp()"
          >
            <v-icon> refresh </v-icon>
          </v-toolbar-side-icon>
        </v-toolbar>
      </v-fade-transition>
      <!-- Main progressbar -->
      <v-progress-linear
        height="5"
        :active="$store.state.common.progress._active"
        :value="$store.state.common.progress.value"
        indeterminate
        class="msr--text toolbar-progressbar"
        :class="{
          'no-toolbar':
            (isLoginPage && $vuetify.breakpoint.xsOnly) || fullscreenMode,
        }"
      />
      <v-content>
        <v-slide-y-transition>
          <update-needed-alert
            v-if="
              $userprofile &&
                !$userprofile.is_being_impersonated &&
                $store.state.common.revisionDiffersFromBackend
            "
          />
        </v-slide-y-transition>
        <v-container
          :class="{'pt-0': isTopLevelPage}"
          :fill-height="isFullHeightPage"
          fluid
        >
          <v-layout column>
            <v-flex
              :class="{
                'desktop-margins':
                  $store.state.common.desktopMode &&
                  showDrawer &&
                  !fullscreenMode,
                'full-height': isFullHeightPage,
              }"
            >
              <transition
                :name="$route.meta.transition || 'page-default-transition'"
                mode="out-in"
              >
                <!-- Note: The key will ensure we re-render on any URL
							       changes, even when they use the same component.
                -->
                <router-view :key="$route.fullPath" />
              </transition>
              <alerts />
            </v-flex>
          </v-layout>
        </v-container>
      </v-content>
      <!-- Make sure imprint and language dropdown is reachable from unauthorized login -->
      <v-footer
        v-if="isLoginPage || isSignupPage"
        app
        class="imprint-footer"
        style="height: auto"
      >
        <v-layout
          class="my-2 mx-2"
          justify-center
          align-center
        >
          <div style="text-align: center">
            <!-- Imprint Link -->
            <small class="mr-3">
              <a
                class="imprint-text grey--text text--darken-2"
                :href="$store.state.common.imprintURL"
              >
                {{ $t('imprint') }}
              </a>
            </small>
            <!-- AGB Link -->
            <small class="mr-3">
              <a
                class="imprint-text grey--text text--darken-2"
                :href="$store.state.common.generalTermsAndConditionsUrl"
              >
                {{ $t('generalTermsAndConditions') }}
              </a>
            </small>
            <!-- Data Privacy Link -->
            <small class="mr-3">
              <a
                class="imprint-text grey--text text--darken-2"
                :href="$store.state.common.dataPrivacyURL"
              >
                {{ $t('dataPrivacy') }}
              </a>
            </small>
            <!-- Wifi Commissioning Link (mobile webview only) -->
            <small
              v-if="$store.state.common.isUsingWebview"
              class="mr-3 nowrap"
            >
              <a
                class="imprint-text grey--text text--darken-2"
                @click="goToWifiCommissioning()"
              >
                {{ $t('landingWifiCommissioningButton') }}
              </a>
            </small>
          </div>
          <!-- Language Select -->
          <div>
            <language-select
              class="language-select"
              small
            />
          </div>
        </v-layout>
      </v-footer>
      <!-- Global Dialogs -->
      <dynamic-dialog />
      <first-login-dialog :show="showFirstLoginDialog" />
      <promote-content-creator-dialog
        :show="showPromoteContentCreatorDialog"
        @close="onPromoteContentCreatorDialogClosed"
      />
      <ask-for-sepa-direct-debit-mandate-dialog
        :show.sync="showAskForSepaDirectDebitMandateDialog"
        @close="onAskForSepaDirectDebitMandateDialogClosed"
      />
      <gema-free-channel-used-dialog
        :show.sync="showGemaFreeChannelUsedDialog"
        @close="onGemaFreeChannelUsedDialogClosed"
      />
    </v-app>
  </div>
</template>

<script>
import {getInitialBrowserLocale} from '@/i18n';
import {LANDING_PAGE_IMAGE} from '@/store';

import {goToWifiCommissioning} from '@/mixins/wifiCommissioning';
import fullscreenMode from '@/mixins/fullscreenMode.js';
import runtime from '@/mixins/runtime.js';
import suppressWarnings from '@/mixins/suppressWarnings.js';
import updateOnContentProcessed from '@/mixins/updateOnContentProcessed.js';

import Alerts from '@/components/Alerts';
import AppDrawer from '@/components/AppDrawer';
import AskForSepaDirectDebitMandateDialog from '@/components/AskForSepaDirectDebitMandateDialog';
import DynamicDialog from '@/components/DynamicDialog';
import FirstLoginDialog from '@/components/FirstLoginDialog';
import GemaFreeChannelUsedDialog from '@/components/GemaFreeChannelUsedDialog';
import LanguageSelect from '@/components/LanguageSelect';
import PromoteContentCreatorDialog from '@/components/PromoteContentCreatorDialog';
import UpdateNeededAlert from '@/components/UpdateNeededAlert';
import UserAvatarImage from '@/components/UserAvatarImage';

export default {
  components: {
    Alerts,
    AppDrawer,
    AskForSepaDirectDebitMandateDialog,
    DynamicDialog,
    FirstLoginDialog,
    GemaFreeChannelUsedDialog,
    LanguageSelect,
    PromoteContentCreatorDialog,
    UpdateNeededAlert,
    UserAvatarImage,
  },
  mixins: [fullscreenMode, runtime, suppressWarnings, updateOnContentProcessed],
  data() {
    return {
      busy: true,
      online: true,
      showAskForSepaDirectDebitMandateDialog: false,
      showGemaFreeChannelUsedDialog: false,
      showPromoteContentCreatorDialog: false,
    };
  },
  computed: {
    userLabel() {
      if (!this.$user) {
        return '';
      }
      const fullName = this.$user.first_name + ' ' + this.$user.last_name;
      if (fullName.trim() == '') {
        return this.$user.email;
      }
      return fullName;
    },
    showFirstLoginDialog() {
      return (
        (this.$store.getters.formRedirectEnabled &&
          this.$store.getters.formRedirectNeeded) ||
        (this.$store.getters.dsgvoRedirectEnabled &&
          this.$store.getters.dsgvoRedirectNeeded) ||
        false
      );
    },
    showToolbar() {
      return (
        !this.fullscreenMode &&
        !this.isLandingPage &&
        !this.isOverlayOptionsDevPage &&
        !(
          (this.isLoginPage || this.isSignupPage) &&
          this.$vuetify.breakpoint.xsOnly
        )
      );
    },
    /** Alias for store attribute so we can assign CSS based on it. */
    showDrawer: {
      get() {
        return this.$store.state.common.showDrawer;
      },
      set(flag) {
        this.$store.commit('SET_SHOW_DRAWER', flag);
      },
    },
    darkToolbar() {
      return !this.isTopLevelPage;
    },
    isLoginPage() {
      return this.$route.name === 'login';
    },
    isOverlayOptionsDevPage() {
      return this.$route.name === 'overlayoptionsdev';
    },
    isLandingPage() {
      return this.$route.name === 'landing';
    },
    isSignupPage() {
      return this.$route.name === 'signup';
    },
    isWifiSetupPage() {
      return this.$route.name === 'wifisetup';
    },
    isMaintenancePage() {
      return this.$route.name === 'maintenance';
    },
    isFullHeightPage() {
      return (
        this.isLandingPage || this.isWifiSetupPage || this.isMaintenancePage
      );
    },
    isTopLevelPage() {
      return (
        this.isLandingPage ||
        this.isLoginPage ||
        this.isSignupPage ||
        this.isMaintenancePage
      );
    },
    elevateToolbar() {
      const isDesktopMode = this.$store.state.common.desktopMode;
      return isDesktopMode && !this.isTopLevelPage;
    },
    /**
     * Return light/dark company logo url for the toolbar.
     */
    toolbarImageURL() {
      if (this.darkToolbar || this.isLandingPage || this.isWifiSetupPage) {
        return '/static/logo_myshopradio_tv_white.svg';
      }
      return '/static/logo_myshopradio_tv_black.svg';
    },
    verySmallDevice() {
      return this.$vuetify.breakpoint.width < 400;
    },
  },
  watch: {
    /**
     * Update background color when navigating to different route.
     */
    '$route.meta.contentAreaBackgroundColor': function () {
      this.defineAppBackgroundStyles();
    },
    /**
     * Watch if we change between Desktop size and lower sizes.
     *
     * Note: Since 1.1.3 this is no triggered on initial load anymore,
     *   so we have an initial call in mounted() as well.
     */
    '$vuetify.breakpoint.lgAndUp': function () {
      this.checkForDesktopMode();
    },
    /**
     * Show an alert when the network goes offline.
     */
    online: function (isOnline) {
      if (isOnline) {
        return;
      }
      this.$bus.$emit('alert-warning', {
        message: 'offlineWarning',
        translate: true,
      });
    },
  },
  mounted() {
    this.$store.dispatch('RETRIEVE_IS_USING_WEBVIEW');
    this.checkForDesktopMode();
    this.defineAppBackgroundStyles();

    this.setInitialLocale();
    this.detectRuntimeEnvironment();
    this.setupAppHotkeys();

    this.retrieveUserAndValidate();
    this.trackOnlineStatus();

    // Fetch revision, store it and compare it regulary to warn the User
    // when he should reload the app when there was an update.
    this.$store.dispatch('RETRIEVE_REVISION').then(() => {
      this.$intervals.registerLongpollingCall(
        this.checkIfWeNeedToReloadTheApp,
        this.$intervals.everyHalfHour
      );
    });

    /**
     * Global (aka 'app-wide') signals registration.
     */
    this.$bus.$on('user-available', this.maybeShowInitialDialogs);
    this.$bus.$on(
      'refetch-on-content-processed',
      this.refetchOnContentProcessed
    );
  },
  beforeDestroy() {
    this.removeAppHotkeys();
    this.$intervals.unregisterAllLongpollingCalls();
  },
  methods: {
    reloadApp() {
      window.location.reload();
    },
    goToWifiCommissioning() {
      const vm = this;
      goToWifiCommissioning(vm);
    },
    maybeShowInitialDialogs() {
      this.checkIfWeNeedToShowPromotionDialog();
      this.checkIfWeNeedToShowAskForSepaDirectDebitMandateDialog();
      this.checkIfWeNeedToShowGemaFreeChannelUsedDialog();
    },
    checkIfWeNeedToShowPromotionDialog() {
      if (this.isSuppressedPromoteContentCreatorDialog()) {
        return;
      }
      if (this.$user && !!this.$userprofile.promote_content_creator_video_url) {
        this.showPromoteContentCreatorDialog = true;
      }
    },
    checkIfWeNeedToShowAskForSepaDirectDebitMandateDialog() {
      if (this.isSuppressedAskForSepaDirectDebitMandateDialog()) {
        return;
      }
      if (
        this.$user &&
        !!this.$userprofile.workspace &&
        this.$userprofile.workspace.ask_for_sepa_direct_debit_mandate &&
        !!this.$userprofile.workspace.sepa_direct_debit_mandate_form_url
      ) {
        this.showAskForSepaDirectDebitMandateDialog = true;
      }
    },
    checkIfWeNeedToShowGemaFreeChannelUsedDialog() {
      if (this.isSuppressedGemaFreeChannelUsedDialog()) {
        return;
      }
      if (
        this.$user &&
        !this.$userprofile.is_association_workspace_user &&
        !!this.$userprofile.uses_gema_free_radio_channel
      ) {
        this.showGemaFreeChannelUsedDialog = true;
      }
    },
    /** Toggle store flag if we need to reload, which will display a warning. */
    checkIfWeNeedToReloadTheApp() {
      this.$store.dispatch('RETRIEVE_REVISION_DIFFERS_FROM_BACKEND');
    },
    checkForDesktopMode() {
      this.$store.commit('SET_DESKTOP_MODE', this.$vuetify.breakpoint.lgAndUp);
    },
    onPromoteContentCreatorDialogClosed() {
      this.showPromoteContentCreatorDialog = false;
      this.suppressPromoteContentCreatorDialog();
    },
    onAskForSepaDirectDebitMandateDialogClosed() {
      this.showAskForSepaDirectDebitMandateDialog = false;
      this.suppressAskForSepaDirectDebitMandateDialog();
    },
    onGemaFreeChannelUsedDialogClosed() {
      this.showGemaFreeChannelUsedDialog = false;
      this.suppressGemaFreeChannelUsedDialog();
    },
    toggleDrawer() {
      this.showDrawer = !this.showDrawer;
    },
    setupAppHotkeys() {
      document.addEventListener('keyup', this.onDocumentKeyup);
    },
    removeAppHotkeys() {
      document.removeEventListener('keyup', this.onDocumentKeyup);
    },
    onDocumentKeyup(event) {
      // Ctrl + Shift + F: Toggle fullscreen mode.
      if (event.ctrlKey && event.shiftKey && event.key == 'F') {
        this.toggleFullscreen();
      }
    },
    setInitialLocale() {
      // We can set the language via query param, remember it from
      // localStorage or use the browser default.
      this.setInitialLocaleFromLocalStorage();
      this.setInitialLocaleFromQuery();
    },
    /** Set locale on i18n and dayjs instances. */
    setInitialLocaleFromLocalStorage() {
      const locale =
        this.$store.getCurrentLocale() || getInitialBrowserLocale();
      if (!locale) {
        return;
      }
      console.log('Using locale:', locale);
      this.$store.commit('SET_LOCALE', locale);
    },
    /**
     * Allow overriding the locale through the URL query.
     *
     * locale (String): A string like 'de' or 'en'. See translations/index.js
     *   for a list of supported locales.
     */
    setInitialLocaleFromQuery() {
      const locale = this.$route.query.locale;
      const availableLocales = Object.keys(this.$i18n.messages);
      const isValidLocale = availableLocales.indexOf(locale) !== -1;
      if (isValidLocale) {
        this.$store.commit('SET_LOCALE', locale);
      }
    },
    updateOnlineStatus() {
      this.online = window.navigator.onLine;
    },
    trackOnlineStatus() {
      window.addEventListener('online', this.updateOnlineStatus);
      window.addEventListener('offline', this.updateOnlineStatus);
    },
    /**
     * When the app is loaded initially (or by refreshing any page),
     * do an async check if our authentication is valid against the
     * API: If not, remove any auth data and redirect to login.
     */
    retrieveUserAndValidate() {
      this.$store
        .dispatch('RETRIEVE_CURRENT_USER')
        .then(() => {
          // Allow other components to react to User fetched event.
          this.$bus.$emit('user-available');

          this.$socketbridge.connect();
          this.maybeShowInitialDialogs();
        })
        .catch(() => {
          console.warn('No token found or invalid.');
          this.$store.commit('UNSET_CURRENT_USER');

          if (
            this.$route.meta.requiresAuth ||
            this.$route.meta.checkAppWrapper
          ) {
            console.warn('Redirecting...');
            console.log(this.$route.path);

            const nextRoute = {
              path: '/login',
              query: {...this.$route.query},
            };
            if (this.$route.path != '/login') {
              nextRoute.query.redirect = this.$route.path;
            }

            // Note: This redirect is only triggered when we are not
            //   logged in already.
            this.handleNativeAppWrapperWebViewLandingPageRedirect(nextRoute);
          }
        });
    },
    handleNativeAppWrapperWebViewLandingPageRedirect(nextRoute) {
      const probablyInAppWrapper =
        this.$store.state.common.runtimeEnv.android ||
        this.$store.state.common.runtimeEnv.iOS;
      if (!probablyInAppWrapper) {
        this.$router.push(nextRoute);
        return;
      }
      this.$api.getIsUsingWebview().then((response) => {
        const isUsingWebview = response.data;
        if (isUsingWebview) {
          nextRoute = {path: '/landing'};
        }
        this.$router.push(nextRoute);
      });
    },
    refetchOnContentProcessed(content) {
      // The SpotDetail page has its own handler for this signal, so
      // avoid handling it twice.
      if (this.$route.name === 'spot') {
        return;
      }
      this.updateStoreRefsOnContentProcessed(content);
    },
    defineAppBackgroundStyles() {
      let style = document.getElementById('app-dynamic-styles') || null;
      if (style) {
        console.log('[DEBUG] Removing previous dynamic app styles');
        style.remove();
      }

      // Combine image with gradient as background: https://stackoverflow.com/a/16589613
      const landingImageWidth = 610;
      const landingImageHeight = 972;
      const landingImagePositionX = 16;
      // Note: This won't be reactive, so a page reload is needed while testing in dev-tools.
      const landingImagePositionY =
        this.$vuetify.breakpoint.height > 667 ? -120 : -220;

      const contentAreaBackgroundColor =
        this.$route.meta.contentAreaBackgroundColor || 'initial';

      const css = `
        main.v-content {
          background-color: ${contentAreaBackgroundColor};
        }

        #app-bg.background {
          background-color: #fbfbfb;
        }

        #app-bg.landing {
          opacity: 1.0;
          background: url('${LANDING_PAGE_IMAGE}'), #2d3033;
          background-size: ${landingImageWidth}px ${landingImageHeight}px;
          background-repeat: no-repeat;
          background-position-x: ${landingImagePositionX}px;
          background-position-y: ${landingImagePositionY}px;
        }
        #app-bg.landing:before {
          content: " ";
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          background: linear-gradient(transparent 33%, #2d3033 77%, #2d3033 100%);
        }

        #app-bg.wifisetup {
          background-color: #000000;
        }
      `;
      style = document.createElement('style');
      style.id = 'app-dynamic-styles';
      style.innerHTML = css;
      document.head.appendChild(style);
    },
  },
};
</script>

<style src="@/mixins/desktopModeStyles.global.stylus" lang="stylus"></style>
<style src="@/mixins/cssGridLayout.css"></style>

<!-- Global helper classes and overrides-->
<style>
/* Root-level CSS Variables */
:root {
  --msr-color-primary: #ff665e;
  --msr-color-secondary: #2d3033;
  --overlay-options-field-bg: rgba(0, 0, 0, 0.04);

  --color-error: rgba(242, 17, 17, 1);
  --color-info: rgba(33, 150, 243, 1);
  --color-success: rgba(76, 175, 80, 1);
  --color-warning: rgba(255, 193, 7, 1);
}

/* To force e.g. an svg icon to a specific target color using a filter.

Via:
  https://isotropic.co/tool/hex-color-to-css-filter/
  https://codepen.io/sosuke/pen/Pjoqqp?editors=0010

*/
.filter-to-msr-color-primary {
  filter: invert(44%) sepia(67%) saturate(607%) hue-rotate(314deg)
    brightness(110%) contrast(101%);
}
.filter-to-white-color {
  filter: brightness(10000%);
}
.filter-to-black-color {
  filter: brightness(0%);
}

/* Additional styles are defined dynamically in defineAppBackgroundStyles() */
#app-bg {
  position: fixed;
  width: 100vw;
  height: 100vh;
}
#app-bg.landing:before {
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0) 60%);
}

.overlay-options-field-bg {
  background-color: var(--overlay-options-field-bg) !important;
}

.v-select-highlight-hint.v-select .v-messages__wrapper .v-messages__message {
  color: var(--msr-color-primary);
  font-size: 14px;
}

.v-badge.smaller .v-badge__badge {
  font-size: 10px;
}
.v-badge.nudged-down .v-badge__badge {
  top: -8px;
}
.v-badge.nudged-right .v-badge__badge {
  right: -26px;
}
.v-badge.nudged-left .v-badge__badge {
  right: -13px;
}

/* Remove the dotted lines below disabled v-text-fields. */
.v-text-custom-disabled.v-input--is-disabled
  .v-input__control
  .v-input__slot:before {
  display: none;
}

/* Remove the dotted lines below v-text-fields with specific class. */
.v-text-no-line-below.v-input .v-input__control .v-input__slot:before {
  display: none;
}

/* Remove the greyish/faded look from disabling the input. */
.v-text-custom-disabled.v-input--is-disabled label {
  color: inherit !important;
}
.v-text-custom-disabled.v-input--is-disabled input {
  color: initial !important;
}
.v-text-custom-disabled .v-icon.v-icon--disabled {
  color: initial !important;
}

/* Customize v-input with box look. */
.v-text-field--box .v-input__control .v-input__slot {
  background: rgba(0, 0, 0, 0.04) !important; /* Original: 0.06 */
}
.v-text-field--box .v-input__control .v-input__slot:hover {
  background: rgba(0, 0, 0, 0.08) !important; /* Original: 0.12*/
}
.v-text-field--box .v-input__control .v-input__slot:hover:before {
  border-color: transparent !important; /* Original: 0.87*/
}
.v-text-field--box .v-input__control .v-input__slot {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}
.v-text-field--box .v-input__control .v-input__slot:before {
  border-color: transparent !important; /* Original: 0.42*/
}

.pseudo-anchor {
  text-decoration: underline;
  cursor: pointer;
}
.pseudo-anchor:hover {
  text-decoration: none;
}

.pseudo-anchor--inverted {
  text-decoration: none;
  cursor: pointer;
}
.pseudo-anchor--inverted:hover {
  text-decoration: underline;
}

.unstyled-anchor {
  color: initial;
  text-decoration: none;
}

.v-text-anchor.v-input input {
  text-decoration: underline;
  pointer-events: initial;
  cursor: pointer;
}

/* Fix v-list-tile content vertically centered instead of starting at top. */
.list-tile-align-start .v-list__tile {
  align-items: flex-start;
}
/* Remove left padding on v-list-tile that is not in container. */
.list-tile-no-left-padding .v-list__tile {
  padding-left: 0;
}

/* Force all dialogs to old default width instead of 100%. */
.v-dialog:not(.v-dialog--fullscreen) {
  width: 290px;
}

/* v0.13 + v0.17 fix: Application background color has changed */
.application.theme--light {
  background: white;
}

/* Fix tile height with two subtitles (e.g. Store) */
a.v-list__tile.v-list__tile--avatar {
  height: auto;
}

/* Adapt height for Store v-list-tiles automatically, so they can fit
   any number of lines for store name, adress etc. */
.v-list .store-tile .v-list__tile {
  height: auto;
}
.v-list .store-tile .v-list__tile .v-list__tile__title {
  height: auto;
}

/* Make v-list-tile selectable if marked as such. */
.v-list .selectable .v-list__tile {
  user-select: text;
}

/* Remove side padding from list tiles that are within a v-container,
   since the v-container already adds lots of padding. */
.container .v-list__tile {
  padding: 0 0;
}

/* Use custom quote characters. */
q.ticker-quote {
  quotes: '\00ab''\00bb';
}

.flipped {
  transform: rotate(-180deg);
}

.ml-auto {
  margin-left: auto !important;
}

.mr-auto {
  margin-right: auto !important;
}

.mlr-auto {
  margin-left: auto !important;
  margin-right: auto !important;
}

.inset-left {
  margin-left: 4.75em;
  width: calc(100% - 4.75em);
}

.inset-left-within-container {
  margin-left: calc(4.75em - 24px);
  width: calc(100% - 42px);
}

.msr--bg-cool-gray {
  background-color: var(--msr-color-secondary) !important;
}

.msr--text-cool-gray {
  color: var(--msr-color-secondary) !important;
}

.msr--text {
  color: var(--msr-color-primary) !important;
}

.msr--text > label.v-label {
  color: var(--msr-color-primary) !important;
}

.msr--text--hover:hover {
  color: var(--msr-color-primary) !important;
}

.msr--border {
  border-color: var(--msr-color-primary) !important;
}

.msr--bold-outline {
  outline: 2px solid var(--msr-color-primary) !important;
}

.msr {
  background-color: var(--msr-color-primary) !important;
}

.bordered {
  border: 1px solid rgba(0, 0, 0, 0.16) !important;
}
.bordered--subtle {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.outlined {
  outline: 1px solid rgba(0, 0, 0, 0.16) !important;
}
.outlined--dark {
  outline: 1px solid rgba(0, 0, 0, 0.32) !important;
}
.outlined--white {
  outline: 1px solid white !important;
}
.outlined--subtle {
  outline: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.no-border-top {
  border-top: none !important;
}
.no-border-bottom {
  border-bottom: none !important;
}
.no-border-left {
  border-left: none !important;
}
.no-border-right {
  border-right: none !important;
}

.bg-success {
  background-color: rgba(76, 175, 80, 0.08) !important;
}

.bg-info {
  background-color: rgba(33, 150, 243, 0.08) !important;
}

.bg-warning {
  background-color: rgba(255, 193, 7, 0.08) !important;
}

.bg-error {
  background-color: rgba(242, 17, 17, 0.08) !important;
}

.border-warning {
  border: 1px solid rgba(255, 193, 7, 0.5) !important;
}

.newbadge {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 0;
  pointer-events: none;
}

.highlighted-badge-text {
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.08em;
}

[v-cloak] {
  display: none;
}

[dbg] {
  outline: 1px solid red !important;
}
[dbg--deep] {
  outline: 1px solid red !important;
}
[dbg--deep] * {
  outline: 1px solid red !important;
}
.dbg {
  outline: 1px solid red !important;
}
.dbg--deep {
  outline: 1px solid red !important;
}
.dbg--deep * {
  outline: 1px solid red !important;
}

.loading-indicator {
  display: flex;
  justify-content: center;
}

.opacity50 {
  opacity: 0.33;
}

.language-select {
  /* Be above e.g. v-snackbar (which uses 1000) */
  z-index: 1010;
}

.clickable {
  cursor: pointer;
}

.unclickable {
  pointer-events: none;
}

.unselectable {
  user-select: none;
}

.wrap {
  white-space: initial;
}

.nowrap {
  white-space: nowrap;
}

.nowrap-overflow-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.grow-0 {
  flex: 0;
}
.grow-025 {
  flex: 0.25;
}
.grow-05 {
  flex: 0.5;
}
.grow-075 {
  flex: 0.75;
}
.grow-1 {
  flex: 1;
}
.grow-2 {
  flex: 2;
}
.grow-3 {
  flex: 3;
}
.grow-4 {
  flex: 4;
}
.grow-5 {
  flex: 5;
}

.z-index-0 {
  z-index: 0 !important;
}

.z-index-1 {
  z-index: 1 !important;
}

.z-index-2 {
  z-index: 2 !important;
}

.z-index-3 {
  z-index: 3 !important;
}

.auto-width {
  width: auto !important;
}

.auto-height {
  height: auto !important;
}

.full-height {
  height: 100% !important;
}

.full-width {
  width: 100% !important;
}

/** Adding to v-list-tile won't affect as it's wrapped in a div, but
    this fixes that so we can apply the class directly to v-list-tile. */
.list-tile-auto-height .v-list__tile {
  height: auto !important;
}

.list-tile-min-height .v-list__tile {
  min-height: 48px;
}

.no-vert-margin {
  margin-top: 0;
  margin-bottom: 0;
}

.no-top-padding {
  padding-top: 0;
}

.no-hover-clr .v-list__tile:hover {
  background: initial !important;
}
.no-click-pointer .v-list__tile:hover {
  cursor: default !important;
}

/* Darker label for radiogroup, switch, checkbox on greyish backgrounds. */
.darker-label .v-label {
  color: rgba(0, 0, 0, 0.87);
}

.sub-icon {
  font-size: 1.1rem !important;
}

/**
 * To ensure objects properly layout on top of each other we sometimes need to add this to the outmost container.
 *
 * Example: Play button overlay image on top of our video poster image element.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_contex
 */
.firefox-stacking-context-fix {
  position: relative;
}

.transparency-checker-background {
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  background-image: linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-repeat: repeat;
}
.transparency-checker-background.small {
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0px;
}

.beta-feature-marker {
  color: var(--msr-color-primary) !important;
  font-size: 0.7rem;
  font-weight: normal;
  position: relative;
  top: -4px;
}
.beta-feature-marker:after {
  content: 'BETA';
}

/* Colorize menu icon when used with custom color class. */
.msr--text .input-group__append-icon {
  color: var(--msr-color-primary) !important;
}

/* Disable scroll inside a tile altogether. */
.v-list__tile__title,
.v-list__tile__sub-title {
  overflow: hidden;
}

/* Remove underline e.g. on v-text-field. */
.no-underline .v-input__slot::before,
.no-underline .v-input__slot::after {
  display: none;
}

/* Horizontal divider rules across device sizes. */
.list-divider-tile {
  margin: 0;
  padding: 0;
  width: calc(100% + 36px + 16px);
  background-color: rgba(0, 0, 0, 0.05);
  position: relative;
  left: -36px;
  display: flex;
  justify-content: center;
}
@media (max-width: 599px) {
  .list-divider-tile {
    left: -24px;
    width: calc(100% + 24px + 24px);
  }
}
.list-divider-subheader {
  font-size: 14px !important;
}

.v-btn:hover .svg-hoverable-icon,
div:hover > .svg-hoverable-icon,
.svg-hoverable-icon:hover {
  filter: invert(44%) sepia(67%) saturate(607%) hue-rotate(314deg)
    brightness(110%) contrast(101%);
}

.v-btn:hover .hoverable-icon,
:hover > .hoverable-icon,
.hoverable-icon:hover {
  color: var(--msr-color-primary) !important;
}

.v-btn:hover .hoverable-icon--info,
:hover > .hoverable-icon--info,
.hoverable-icon--info:hover {
  color: #2196f3 !important;
}

.v-btn:hover .hoverable-icon--success,
:hover > .hoverable-icon--success,
.hoverable-icon--success:hover {
  color: #4caf50 !important;
}

.v-btn:hover .hoverable-icon--error,
:hover > .hoverable-icon--error,
.hoverable-icon--error:hover {
  color: #f21111 !important;
}

/* Use normal width/height and icon position for button in snackbar.
   This is used in the EntrySelectionOverlay component. */
.v-snack__content .v-btn {
  min-width: 88px;
  height: 36px;
  justify-content: center !important;
}

/* TODO: Remove below once updated to 1.5.x. */
/* Fix clicking an event dot closing the datepicker without selecting
   the associated date: Seems to have been fixed somewhere above 1.3.5
   in which this has been monitored. */
.v-date-picker-table__event {
  z-index: -1;
}

@media print {
  .print .application {
    background: none;
  }
  .print aside,
  .print .v-alert,
  .print .v-btn--fixed,
  .print .v-toolbar {
    display: none;
  }
  .print #app-bg,
  .print .shadow-box,
  .print .nav-wrapper,
  .print .v-btn--fixed,
  .print .tools,
  .print .tools-fixed,
  .print .tools-fixed-spacer {
    display: none !important;
  }
  .print .container,
  .print .v-content {
    padding: 0 !important;
    max-width: none;
  }
  .print div,
  .print [class^='elevation-'] {
    box-shadow: none !important;
  }
  .print img,
  .print .v-image,
  .print .spot-tile,
  .print .store-tile,
  .print .retailer-timespans-list,
  .print .campaign-tile {
    page-break-inside: avoid;
  }
  .print .apexcharts-toolbar {
    display: none;
  }
}
</style>

<!-- Scoped CSS -->
<style scoped>
.toolbar-logo {
  height: 32px;
}

.toolbar-progressbar {
  margin: 0;
  top: 48px;
}
.toolbar-progressbar.no-toolbar {
  top: 0px;
}

.imprint-footer {
  background: #fbfbfb !important;
  z-index: 5;
  display: flex;
  justify-content: center;
}

.imprint-text {
  text-transform: uppercase;
  text-decoration: none;
}
.imprint-text:hover {
  color: var(--msr-color-primary) !important;
}

/* Custom page transitions */
.page-default-transition-enter-active,
.page-default-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); /* aka 'swing' */
}
.page-default-transition-enter,
.page-default-transition-leave-to {
  opacity: 0;
}

.page-slide-x-transition-enter-active,
.page-slide-x-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); /* aka 'swing' */
}
.page-slide-x-transition-enter,
.page-slide-x-transition-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}
</style>



// WEBPACK FOOTER //
// src/App.vue